import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { AppUtils } from '../../../core/config/app.utils';
import { UserService } from '../../../core/services/user.service';
import { AuthService } from '../../../modules/auth/services/auth.service';
import { NzUploadXHRArgs } from 'ng-zorro-antd/upload';
import { FilesService } from '../../../core/services/files.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ActionAlert } from '../../../core/dto/actionAlert';

const USER_PROFILE_IMAGE_UPLOAD = 'USER_PROFILE_IMAGE_UPLOAD';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent implements OnInit, OnDestroy {
  breadcrumbs = [
    {
      title: 'User Profile',
    },
  ];
  changePswForm: FormGroup | any;
  formErrors: any;
  oldPswVisible = true;
  newPswVisible = true;
  isSamePwd = true;
  confirmPswVisible = true;
  userData: any;

  isVisibleEditName = false;
  editDataButtonLoading = false;
  editDataType = '';

  //editable Values
  firstname = '';
  lastname = '';
  mobile = '';
  uploadImageUrl: any;
  actionMessage = new ActionAlert();

  loading = false;
  imageUploading = false;
  scrollInterval: any;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private authService: AuthService,
    private filesService: FilesService,
    private elementRef: ElementRef,
    private notification: NzNotificationService,
    private message: NzMessageService,
  ) {
    this.formErrors = {
      oldPassword: {},
      password: {},
      confirmPassword: {},
    };
  }

  ngOnInit() {
    this.loading = true;
    this.initChangePswForm();
    this.getUserData();
    this.filesService.fileUploadingSubject.subscribe((res: any) => {
      if (res.type === USER_PROFILE_IMAGE_UPLOAD) {
        this.imageUploading = res.loading;
      }
    });
    this.scrollInterval = setInterval(() => {
      this.isFirstLogCheck();
    }, 400);
  }

  initChangePswForm(): void {
    this.changePswForm = this.fb.group({
      oldPassword: [null, Validators.required],
      password: [null, Validators.required],
      confirmPassword: [
        null,
        [Validators.required, this.confirmationValidator],
      ],
    });

    this.changePswForm.valueChanges.subscribe((form: any) => {
      this.formErrors = AppUtils.getFormErrors(
        this.changePswForm,
        this.formErrors,
      );
    });
  }

  confirmationValidator = (control: FormControl): { [s: string]: boolean } => {
    if (!control.value) {
      this.isSamePwd = true;
      return { required: true };
    } else if (control.value !== this.changePswForm.controls.password.value) {
      this.isSamePwd = false;
      return { confirm: true, error: true };
    }
    this.isSamePwd = true;
    return {};
  };

  validateMobile(input: any): boolean {
    return isNaN(input);
  }

  getUserData(): void {
    this.loading = true;
    this.userService
      .getOneUserWithDetails(this.authService.getLoggedInUser().userId)
      .then((res: any) => {
        this.loading = false;
        this.userData = res;
        this.uploadImageUrl = this.userData?.imageUrl;
        this.userService.loggedInUserNameSubject.next(
          `${this.userData?.firstname} ${this.userData?.lastname}`,
        );
        this.userService.loggedInUserProfilePicSubject.next(
          this.userData?.imageUrl ? this.userData?.imageUrl : '',
        );
        // if (this.userData.isFirstLogged) {
        //   this.scrollToPwsChange();
        //   this.pwsChangeNotification();
        // this.userService.updateUser({
        //   _id: this.userData?._id,
        //   isFirstLogged: false,
        // });
        // }
      });
  }
  isFirstLogCheck() {
    if (this.userData?.isFirstLogged) {
      clearInterval(this.scrollInterval);
      this.scrollToPwsChange();
      this.pwsChangeNotification();
      this.userService
        .updateUser({
          _id: this.userData?._id,
          isFirstLogged: false,
        })
        .then(() => {
          this.getUserData();
        });
    } else if (this.userData && !this.userData?.isFirstLogged) {
      clearInterval(this.scrollInterval);
    }
  }
  scrollToPwsChange(): void {
    const element = this.elementRef.nativeElement.querySelector('#pwsChange');
    element?.scrollIntoView({ behavior: 'smooth' });
  }
  pwsChangeNotification(): void {
    this.notification.warning(
      'Change Your Password',
      'Change your auto generated password by the system for your privacy.',
      { nzPlacement: 'top' },
    );
  }

  handleCancelEditName(): void {
    this.isVisibleEditName = false;
  }

  showNameEditModal(type: string): void {
    this.isVisibleEditName = true;
    switch (type) {
      case 'name':
        this.editDataType = 'name';
        this.firstname = this.userData?.firstname;
        this.lastname = this.userData?.lastname;
        break;
      case 'mobile':
        this.editDataType = 'mobile';
        this.mobile = this.userData?.mobile;
        break;
    }
  }

  saveData(type: any): void {
    this.loading = true;
    let payload = {};
    this.editDataButtonLoading = true;
    if (type === 'name') {
      payload = {
        _id: this.userData?._id,
        firstname: this.firstname,
        lastname: this.lastname,
      };
    } else {
      payload = {
        _id: this.userData?._id,
        mobile: this.mobile,
      };
    }
    this.userService.updateUser(payload).then(() => {
      this.editDataButtonLoading = false;
      this.isVisibleEditName = false;
      switch (type) {
        case 'name':
          this.message.success('User name has been updated!');
          break;
        case 'mobile':
          this.message.success('Mobile number has been updated!');
          break;
      }
      this.userService.getOneUserWithDetails(this.userData?._id).then(() => {
        this.getUserData();
      });
    });
  }

  async changePwd(): Promise<any> {
    this.loading = true;
    try {
      const formData = this.changePswForm.getRawValue();
      const res = await this.authService.changePwd({
        id: this.userData?._id,
        ...formData,
      });
      this.message.success(res.message);
      this.authService.logout();
      this.loading = false;
    } catch (e: any) {
      if (e.oldPwdCheck) {
        this.actionMessage = new ActionAlert(true, e.message, 'error');
      }
      this.loading = false;
    }
  }

  customUploadReq = (item: NzUploadXHRArgs) =>
    this.filesService
      .postFile(item.file, USER_PROFILE_IMAGE_UPLOAD)
      .subscribe((res) => {
        const data = res as any;
        this.userService
          .updateUser({ _id: this.userData?._id, imageUrl: data.url })
          .then(() => {
            this.filesService.fileUploadingSubject.next({
              type: USER_PROFILE_IMAGE_UPLOAD,
              loading: false,
            });
            this.message.success('Image has been uploaded!');
            this.getUserData();
          });
      });

  ngOnDestroy(): void {
    clearInterval(this.scrollInterval);
  }
}
